import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useStandaloneKycMetadata } from '@dealstack/frontend/investor/pages/StandaloneKyc/hooks/useStandaloneKycMetadata';
import { useStandaloneKycSteps } from '@dealstack/frontend/investor/pages/StandaloneKyc/hooks/useStandaloneKycSteps';
import { useStandaloneKycContext } from '@dealstack/frontend/investor/pages/StandaloneKyc/hooks/useStandaloneKycContext';
import { useGetInvestorKycForms } from '@dealstack/frontend/investor/pages/Investment/hooks/useGetInvestorKycForms';
import { DocumentWorkflowTemplateVisibleIn } from '@dealstack/shared/lib/generated';

export const useStandaloneKycController = () => {
	const { kycWorkflowId } = useParams<{ kycWorkflowId: string }>();

	const metadataHook = useStandaloneKycMetadata(kycWorkflowId);
	const { data: attachedKycForms } = useGetInvestorKycForms(kycWorkflowId ?? '', {
		enabled: !!kycWorkflowId,
	});
	const hasAudienceForms = useMemo(() => {
		if (attachedKycForms === undefined) {
			return undefined;
		}
		const audienceVisibleIn = metadataHook.isPiv
			? DocumentWorkflowTemplateVisibleIn.KYC_VEHICLE
			: DocumentWorkflowTemplateVisibleIn.KYC_INDIVIDUAL;
		return attachedKycForms.some((form) => form.visibleIn.includes(audienceVisibleIn));
	}, [attachedKycForms, metadataHook.isPiv]);
	const stepsHook = useStandaloneKycSteps(
		metadataHook.kycSetting,
		metadataHook.isPiv,
		metadataHook.customDetailsQuery.data,
		hasAudienceForms
	);
	const contextValue = useStandaloneKycContext(metadataHook, stepsHook);

	return {
		contextValue,
		isLoading: metadataHook.isLoading,
		mipName: metadataHook.metadata?.mipName ?? '',
		activeStep: stepsHook.activeStep,
		activeStepKey: stepsHook.activeStepKey,
		onNextStep: stepsHook.onNextStep,
		onPreviousStep: stepsHook.onPreviousStep,
		isContinueDisabled: stepsHook.isContinueDisabled,
		isLastStep: stepsHook.isLastStep,
		sideBarItems: stepsHook.sideBarItems,
		onSidebarNavigation: stepsHook.onSidebarNavigation,
	};
};
